import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { authApi } from '@/lib/api'

/**
 * OAuth Callback Page - Session-based Authentication
 *
 * This page handles the OAuth redirect from the backend after successful authentication.
 *
 * Flow:
 * 1. Backend redirects here with success=true&provider=google/github
 * 2. Session cookie is automatically sent with requests (httpOnly, secure)
 * 3. Fetch fresh user data from API using session authentication
 * 4. Update auth context with user data
 * 5. Redirect to home page
 *
 * Key difference from JWT approach:
 * - No token extraction from URL
 * - Session cookie handled automatically by browser
 * - Just need to validate session and fetch user data
 */
export default function OAuthCallback() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login } = useAuth()

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Check if OAuth was successful
        const success = searchParams.get('success')
        const provider = searchParams.get('provider')

        if (!success || success !== 'true') {
          console.error('OAuth callback failed: No success parameter')
          navigate('/login?error=oauth_failed', { replace: true })
          return
        }

        console.log(`OAuth successful via ${provider}, fetching user data...`)

        // Fetch fresh user data from API
        // Session cookie is automatically sent by browser
        await authApi.csrf() // Ensure we have a fresh CSRF token
        const response = await authApi.me()

        if (!response.data?.user) {
          throw new Error('Failed to fetch user data')
        }

        const userData = response.data.user

        console.log('User data fetched successfully:', userData.username)

        // Update auth context with user data
        login(userData)

        // Redirect to dashboard
        navigate('/dashboard', { replace: true })
      } catch (error) {
        console.error('OAuth callback failed:', error)
        navigate('/login?error=oauth_failed', { replace: true })
      }
    }

    handleOAuthCallback()
  }, [navigate, login, searchParams])

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="space-y-4 text-center">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Completing OAuth login...</p>
      </div>
    </div>
  )
}
